import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch, useAppSelector } from '../../redux/Store';
import { updateThemePreference, fetchSoundPreferences } from '../../redux/thunks/userPreferencesThunks';
import { fetchMessageThreads } from '../../redux/thunks/messagesThunks';
import { fetchUnreadNotificationCount } from '../../redux/thunks/notificationsThunks';
import { addNotification } from '../../redux/slices/notificationsSlice';
import { receiveSocketMessage } from '../../redux/slices/messagesSlice';
import { useSocket } from '../../contexts/SocketContext';
import soundManager, { NotificationSoundType } from '../../utils/SoundManager';
import {
  FaBars,
  FaSearch,
  FaBell,
  FaEnvelope,
} from 'react-icons/fa';
import logo from '../../assets/klyx-logo1.png';
import logoMobile from '../../assets/klyx-logo2.png';
import HamburgerMenu from './HamburgerMenu';
import MessagesPanel from './MessagesPanel';
import NotificationsPanel from './NotificationsPanel';
import UserMenu from './UserMenu';
import SearchBar from './SearchBar';
import './AppBar.scss';

// Routes where AppBar should NOT be displayed. `/` used to be
// here from when home was a walled marketing page; removed after
// ADR-007's public-first push so anon visitors see consistent
// chrome (logo, search, sign-in/up) across every marketing
// surface — /home, /about, /creators, /leaderboards, etc.
// Linear-onboarding routes that intentionally hide the chrome so
// users focus on the single next step. /login + /signup were
// previously in this list but got promoted back to full chrome
// so anon visitors keep their nav context (they can bounce to
// /about, /agents/discover, etc. mid-flow without losing state).
const EXCLUDED_ROUTES = ['/forgot-password', '/verify-email', '/create-profile'];

interface AppBarProps {
  onThemeToggle?: (isDark: boolean) => void;
}

export const AppBar: React.FC<AppBarProps> = ({ onThemeToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const messageThreads = useSelector((state: RootState) => state.messages.threads);

  // UI State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  // Theme persists in localStorage so refreshes + fresh tabs don't
  // revert to dark (previous behavior). For authed users, the server
  // preference syncs via updateThemePreference in handleThemeToggle
  // + can flow back via a hydration effect elsewhere. localStorage is
  // the local truth so anon users get persistence too.
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('klyx-theme');
      if (stored === 'light') return false;
      if (stored === 'dark') return true;
    } catch {
      // localStorage can throw in private mode / disabled storage —
      // fall through to the dark default.
    }
    return true; // default: dark
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Calculate unread thread count (number of threads with unread messages)
  const unreadThreadCount = messageThreads.filter(thread => thread.unreadCount > 0).length;

  // Unread notification count from Redux (updated by slice reducers + socket)
  const unreadNotifications = useAppSelector((state) => state.notifications.unreadCount);

  const { socket } = useSocket();

  // Track previous counts to detect new notifications (null = not initialized)
  const prevUnreadThreadCount = useRef<number | null>(null);
  const prevUnreadNotifications = useRef<number | null>(null);

  // Check if AppBar should be shown on current route
  // ADR-007 PR 5: anon on public content pages (profile, post,
  // achievements, leaderboards) still needs chrome — otherwise there's
  // no way to sign up, no home button, no search. Drop the
  // `isAuthenticated &&` gate; the render below branches on
  // isAuthenticated for the right-side controls (authed → messages /
  // notifications / user menu; anon → sign-in / sign-up).
  const shouldShowAppBar = !EXCLUDED_ROUTES.includes(location.pathname);

  // Add/remove body class to control padding when AppBar is visible
  useEffect(() => {
    if (shouldShowAppBar) {
      document.body.classList.add('app-bar-visible');
    } else {
      document.body.classList.remove('app-bar-visible');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('app-bar-visible');
    };
  }, [shouldShowAppBar]);

  // Handle window resize for mobile logo switching
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize sound manager and load user preferences
  useEffect(() => {
    if (!isAuthenticated) return;

    const initializeSounds = async () => {
      // Initialize sound manager
      await soundManager.initialize();

      // Load user's sound preferences
      try {
        const preferences = await dispatch(fetchSoundPreferences()).unwrap();
        if (preferences) {
          soundManager.updatePreferences(preferences);
        }
      } catch (error) {
        console.log('Using default sound preferences');
      }
    };

    initializeSounds();

    // Enable audio playback on any user interaction
    const enableAudio = () => {
      soundManager.enableAudioPlayback();
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };

    document.addEventListener('click', enableAudio, { once: true });
    document.addEventListener('keydown', enableAudio, { once: true });

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, [isAuthenticated, dispatch]);

  // Fetch message threads and notifications on mount
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchInitialData = async () => {
      try {
        await Promise.all([
          dispatch(fetchMessageThreads()).unwrap(),
          dispatch(fetchUnreadNotificationCount()).unwrap(),
        ]);
        prevUnreadThreadCount.current = unreadThreadCount;
        prevUnreadNotifications.current = unreadNotifications;
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, [isAuthenticated, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Map a notification type to the sound key it should trigger.
  // The notification type space (see NotificationService.ts) is wider
  // than the sound type space — this maps the full set so nothing
  // silently falls to 'system' (which is what was happening for
  // every paid touchpoint except trade royalties: PPV / view fee /
  // subscription purchases all came through silently).
  const notificationSoundFor = (type: string | undefined): NotificationSoundType => {
    switch (type) {
      case 'like':
      case 'dislike':
        return 'like';
      case 'comment':
        return 'comment';
      case 'mention':
        return 'mention';
      case 'follow':
        return 'follow';
      // Anything the creator gets paid for — chime with the
      // purchase sound. Trade royalty, profile sale/purchase,
      // PPV sale, view-fee unlock, subscription, and the receive-
      // side spotlight (someone paid to spotlight your content).
      case 'profile_purchase':
      case 'profile_sale':
      case 'royalty_earned':
      case 'ppv_sale':
      case 'view_fee_sale':
      case 'subscription_sale':
      case 'spotlight_received':
        return 'purchase';
      case 'message':
        return 'message';
      // Achievement unlocks deserve a noticeable chime but we
      // don't have a dedicated achievement sound yet. Reuse the
      // purchase tone — it's the closest "celebratory" cue.
      case 'achievement_earned':
        return 'purchase';
      // Subscribed-creator publishes a new post — same neutral cue as
      // generic system; the subscriber didn't move money, they're just
      // being told there's something to read. Explicit case (rather
      // than the default fall-through) so future tweaks have a clear
      // anchor.
      case 'new_post_from_subscription':
        return 'system';
      // 'spotlight_sent', 'profile_view', explicit 'system', and
      // anything new the backend adds later fall to 'system'.
      // profile_view in particular can be frequent; keep it quiet.
      default:
        return 'system';
    }
  };

  // Listen for real-time notifications via socket
  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notification: any) => {
      dispatch(addNotification({
        id: notification.id || `socket-${Date.now()}`,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        timestamp: notification.timestamp || new Date().toISOString(),
        read: false,
        avatar: notification.avatar,
        link: notification.link,
      }));
      // Play the type-specific sound right at receive time so we don't rely
      // on Redux unreadCount diffing (which can swallow rapid bursts).
      soundManager.play(notificationSoundFor(notification.type));
    };

    const handleMessageReceived = (msg: any) => {
      if (!msg?.id || !msg?.threadId) return;
      dispatch(receiveSocketMessage({
        id: msg.id,
        threadId: msg.threadId,
        content: msg.content,
        senderId: msg.senderId,
        timestamp: msg.timestamp || new Date().toISOString(),
      }));
      // Sender's own echo shouldn't ding them. If the server forwards it back
      // for some reason, the unread diff still won't move because the active
      // thread is theirs — but we also gate by senderId vs current user here.
      if (msg.senderId && msg.senderId !== user?.id) {
        soundManager.play('message');
      }
    };

    socket.on('new-notification', handleNewNotification);
    socket.on('message-received', handleMessageReceived);
    return () => {
      socket.off('new-notification', handleNewNotification);
      socket.off('message-received', handleMessageReceived);
    };
  }, [socket, dispatch, user?.id]);

  // Backup sound triggers: if the socket handlers above somehow miss an event
  // (e.g. an in-app action that bumps the count without an emit), still chime
  // on a count increase. Skips the first run so hydration from /me doesn't
  // play sounds.
  useEffect(() => {
    if (prevUnreadThreadCount.current !== null && unreadThreadCount > prevUnreadThreadCount.current) {
      soundManager.play('message');
    }
    prevUnreadThreadCount.current = unreadThreadCount;
  }, [unreadThreadCount]);

  useEffect(() => {
    if (prevUnreadNotifications.current !== null && unreadNotifications > prevUnreadNotifications.current) {
      soundManager.play('system');
    }
    prevUnreadNotifications.current = unreadNotifications;
  }, [unreadNotifications]);

  // Handle scroll behavior - hide on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        // Always show at top of page
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past threshold
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close panels when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside app-bar and all panels
      if (
        !target.closest('.app-bar') &&
        !target.closest('.notifications-panel') &&
        !target.closest('.messages-panel') &&
        !target.closest('.hamburger-menu')
      ) {
        setIsMenuOpen(false);
        setIsMessagesOpen(false);
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeToggle = async () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    onThemeToggle?.(newTheme);

    // Persist locally — every user (anon + authed) gets refresh-
    // stable theme via this. Wrapped in try/catch because
    // localStorage throws in some private-mode / storage-disabled
    // contexts and losing theme persistence shouldn't break the
    // click.
    try {
      localStorage.setItem('klyx-theme', newTheme ? 'dark' : 'light');
    } catch {
      /* private mode / storage disabled — theme won't persist this session */
    }

    // Sync to server for cross-device consistency (authed only).
    if (isAuthenticated) {
      try {
        await dispatch(updateThemePreference(newTheme ? 'dark' : 'light'));
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  const handleLogoClick = () => {
    // Anon → home. Authed → dashboard. Anon clicking the logo on a
    // public content page should land somewhere useful, not on the
    // walled dashboard.
    navigate(isAuthenticated ? '/dashboard' : '/');
  };

  const handleMessagesClick = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      navigate('/messages');
    } else {
      setIsMessagesOpen(!isMessagesOpen);
      setIsNotificationsOpen(false);
    }
  };

  const handleNotificationsClick = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
    setIsMessagesOpen(false);
  };

  const formatBadgeCount = (count: number): string => {
    if (count > 99) return '99+';
    return count.toString();
  };

  if (!shouldShowAppBar) {
    return null;
  }

  return (
    <>
      <header className={`app-bar ${isVisible ? 'visible' : 'hidden'}`}>
        <div className="app-bar-container">
          {/* Left Section */}
          <div className="app-bar-left">
            {/* Hamburger Menu — always shown. The drawer filters its
                own items by auth state (anon sees public links +
                Sign in/Sign up; authed sees the full nav). Matters
                especially on mobile where the inline nav wraps or
                gets truncated. */}
            <button
              className="app-bar-icon-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Menu"
            >
              <FaBars />
            </button>

            {/* Logo — always clickable; sends home for anon, dashboard for authed. */}
            <div className="app-bar-logo" onClick={handleLogoClick}>
              <img src={isMobile ? logoMobile : logo} alt="Klyx" />
              <span className="app-bar-brand"></span>
            </div>
          </div>

          {/* Center Section — Search (Desktop). Authed-only:
              every /api/search endpoint requires auth, so exposing
              the input to anon would silently 401 on every query.
              Revisit if a public-search project ships. */}
          {isAuthenticated && (
            <div className="app-bar-center">
              <SearchBar />
            </div>
          )}

          {/* Right Section */}
          <div className="app-bar-right">
            {/* Search Icon (Mobile) — authed-only for the same
                reason as the desktop SearchBar above. */}
            {isAuthenticated && (
              <button
                className="app-bar-icon-btn mobile-only"
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                aria-label="Search"
              >
                <FaSearch />
              </button>
            )}

            {/* Authed right-side chrome: messages, notifications, user menu. */}
            {isAuthenticated ? (
              <>
                <button
                  className="app-bar-icon-btn"
                  onClick={handleMessagesClick}
                  aria-label={`Messages (${unreadThreadCount} unread conversations)`}
                >
                  <FaEnvelope />
                  {unreadThreadCount > 0 && (
                    <span className="app-bar-badge">{formatBadgeCount(unreadThreadCount)}</span>
                  )}
                </button>

                <button
                  className="app-bar-icon-btn"
                  onClick={handleNotificationsClick}
                  aria-label={`Notifications (${unreadNotifications} unread)`}
                >
                  <FaBell />
                  {unreadNotifications > 0 && (
                    <span className="app-bar-badge">{formatBadgeCount(unreadNotifications)}</span>
                  )}
                </button>

                <UserMenu user={user} isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
              </>
            ) : (
              /* Anon: About + sign in / sign up. The sign-in/up pair
                 is the load-bearing chrome for turning an anon visitor
                 into a signup (wedge principle, ADR-007 PR 5). About
                 sits alongside so the "what is this?" pitch is one
                 click from any marketing page — otherwise anon has to
                 land on /home first to find the /about link. */
              <>
                {/* Slim anon nav: About + Sign in only. Everything
                    else — Discover Agents, Leaderboards, Sign up —
                    lives in the hamburger drawer so mobile widths
                    don't crowd + the app bar stays clean. */}
                <button
                  className="app-bar-btn-secondary"
                  onClick={() => navigate('/about')}
                >
                  About
                </button>
                <button
                  className="app-bar-btn-primary"
                  onClick={() => navigate('/login', { state: { from: location.pathname + location.search } })}
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Search Overlay */}
        {isSearchOpen && (
          <div className="mobile-search-overlay">
            <SearchBar onClose={() => setIsSearchOpen(false)} mobile />
          </div>
        )}
      </header>

      {/* HamburgerMenu is anon-viewable — the drawer branches on
          auth state internally (public links + Sign in/Sign up for
          anon; full nav for authed). */}
      <HamburgerMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />

      {/* Messages + Notifications are authed-only — anon has no
          trigger button, and rendering them would fire redux
          selectors that assume an authed user shape. */}
      {isAuthenticated && (
        <>
          <MessagesPanel isOpen={isMessagesOpen} onClose={() => setIsMessagesOpen(false)} />
          <NotificationsPanel
            isOpen={isNotificationsOpen}
            onClose={() => setIsNotificationsOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default AppBar;
